<template>
  <div class="container-fluid batch-processing-container">
  <div class="card shadow rounded-3 mt-4 p-0 border-0">
    <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center py-3">
      <h5 class="fw-bold mb-0">Batch Processing</h5>
      <div class="d-flex align-items-center gap-2">
        <span v-if="active_batch_job" class="live-indicator">
          <span class="live-dot"></span>
          Live
        </span>
      </div>
    </div>

    <div class="card-body">
      <!-- Tab Navigation -->
      <SlideTabComponent
        :tabs="tabs"
        v-model="current_tab"
      />

      <!-- Upload Tab Content -->
      <div v-if="current_tab === 'upload'" class="tab-content mt-4">
        <!-- Workflow Info -->
        <div class="batch-card p-4 mb-4">
          <h6 class="fw-bold text-xs mb-3">
            <i class="fas fa-sitemap me-2 text-primary"></i>
            Workflow Information
          </h6>
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label text-xs fw-medium text-muted">Workflow</label>
              <div class="form-control text-sm rounded-3 bg-light">
                <i class="fas fa-project-diagram me-2 text-primary"></i>
                {{ current_workflow_name || 'Loading...' }}
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label text-xs fw-medium text-muted">Batch Type</label>
              <div class="form-control text-sm rounded-3 bg-light">
                <i :class="batch_type === 'individual' ? 'fas fa-user' : 'fas fa-building'" class="me-2 text-primary"></i>
                {{ batch_type === 'individual' ? 'Individual Verification' : 'Company Onboarding' }}
              </div>
            </div>
          </div>
        </div>

        <!-- File Upload Zone -->
        <div 
          class="upload-zone d-flex flex-column align-items-center justify-content-center p-5 mb-4"
          :class="{ 'has-file': batch_file, 'drag-over': is_dragging }"
          @dragover.prevent="is_dragging = true"
          @dragleave.prevent="is_dragging = false"
          @drop.prevent="handle_drop($event)"
          v-if="workflow_id">
          
          <input 
            type="file" 
            ref="fileInput" 
            @change="handle_file_upload" 
            accept=".csv,.xlsx,.xls" 
            class="d-none" 
          />
          
          <!-- No file selected -->
          <div v-if="!batch_file">
            <div class="text-center py-4">
              <div class="mb-4">
                <i class="fas fa-cloud-upload-alt fa-4x text-primary opacity-75"></i>
              </div>
              <h6 class="fw-bold mb-2">Drop your file here</h6>
              <p class="text-muted text-sm mb-2">or click to browse from your computer</p>
              <p class="text-muted text-xs mb-4">
                Supports: CSV, Excel (.xlsx, .xls) | Max {{ RECORD_LIMIT }} records
              </p>
              <button 
                type="button" 
                class="btn btn-sm bg-green rounded-3 shadow-sm text-white fw-bold px-4"
                @click="$refs.fileInput.click()">
                <i class="fas fa-folder-open me-2"></i>
                Browse Files
              </button>
            </div>
          </div>
          
          <!-- File selected -->
          <div v-else>
            <div class="file-info-card d-flex align-items-center w-100 text-xs" style="max-width: 500px;">
              <div class="me-3">
                <i class="fas fa-file-excel fa-3x text-success"></i>
              </div>
              <div class="flex-grow-1">
                <h6 class="fw-bold mb-1 text-truncate">{{ batch_file?.name || '' }}</h6>
                <p class="text-muted text-xs mb-0">{{ format_file_size(batch_file?.size || 0)}}</p>
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-danger rounded-3" @click="remove_file">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Workflow not loaded message -->
        <div v-if="!workflow_id" class="batch-card p-5 text-center mb-4">
          <div class="empty-state-icon">
            <i class="fas fa-hand-pointer fa-2x text-muted"></i>
          </div>
          <h6 class="fw-bold mb-2">Loading Workflow</h6>
          <p class="text-muted text-sm mb-0">Please wait while we load the workflow information</p>
        </div>

        <!-- Column Mapping Section -->
        <div v-if="batch_file && detected_columns.length > 0" class="batch-card p-4 mb-4">
          <h6 class="fw-bold text-xs mb-3">
            <i class="fas fa-columns me-2 text-primary"></i>
            Column Mapping
          </h6>
          
          <div class="batch-alert batch-alert-info mb-4">
            <i class="fas fa-info-circle"></i>
            <div class="text-xs">
              <strong>Auto-detected columns.</strong> Verify the mapping below matches your file structure.
              Required fields for this workflow: {{ requiredFields.join(', ') || 'none' }}.
            </div>
          </div>

          <!-- Individual Fields -->
          <div v-if="batch_type === 'individual'" class="row g-3">
            <div class="col-md-4">
              <label class="form-label text-xs fw-medium text-muted">{{ getFieldLabel('id_number', 'ID Number Column') }}</label>
              <select v-model="id_number_column" class="form-select text-sm rounded-3" @change="extract_records">
                <option value="">Select column...</option>
                <option v-for="col in detected_columns" :key="col" :value="col">{{ col }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label text-xs fw-medium text-muted">{{ getFieldLabel('first_name', 'First Name Column') }}</label>
              <select v-model="first_name_column" class="form-select text-sm rounded-3" @change="extract_records">
                <option value="">Select column...</option>
                <option v-for="col in detected_columns" :key="col" :value="col">{{ col }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label text-xs fw-medium text-muted">{{ getFieldLabel('last_name', 'Last Name Column') }}</label>
              <select v-model="last_name_column" class="form-select text-sm rounded-3" @change="extract_records">
                <option value="">Select column...</option>
                <option v-for="col in detected_columns" :key="col" :value="col">{{ col }}</option>
              </select>
            </div>
          </div>

          <!-- Company Fields -->
          <div v-if="batch_type === 'company'" class="row g-3">
            <div class="col-md-6">
              <label class="form-label text-xs fw-medium text-muted">{{ getFieldLabel('name', 'Company Name Column') }}</label>
              <select v-model="company_name_column" class="form-select text-sm rounded-3" @change="extract_records">
                <option value="">Select column...</option>
                <option v-for="col in detected_columns" :key="col" :value="col">{{ col }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label text-xs fw-medium text-muted">{{ getFieldLabel('registration_number', 'Registration Number Column') }}</label>
              <select v-model="registration_number_column" class="form-select text-sm rounded-3" @change="extract_records">
                <option value="">Select column...</option>
                <option v-for="col in detected_columns" :key="col" :value="col">{{ col }}</option>
              </select>
            </div>
          </div>

          <div v-if="batch_capabilities.zoho_reference_mapping_enabled" class="row g-3 mt-1">
            <div class="col-12">
              <div class="batch-alert batch-alert-info mb-0">
                <i class="fas fa-link"></i>
                <div class="text-xs">
                  <strong>Optional Zoho mapping enabled.</strong>
                  You can map a source column to existing Zoho record IDs so batch export uses those IDs directly.
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label text-xs fw-medium text-muted">Zoho Record ID Column (Optional)</label>
              <select v-model="zoho_reference_column" class="form-select text-sm rounded-3" @change="extract_records">
                <option value="">Do not map</option>
                <option v-for="col in detected_columns" :key="`zoho-${col}`" :value="col">{{ col }}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Data Preview Section -->
        <div v-if="extracted_records.length > 0" class="batch-card mb-4">
          <div class="d-flex justify-content-between align-items-center p-4 border-bottom">
            <div>
              <h6 class="fw-bold text-xs mb-1">
                <i class="fas fa-table me-2 text-primary"></i>
                Data Preview
              </h6>
              <p class="text-muted text-xs mb-0">Review your data before processing</p>
            </div>
            <div class="d-flex gap-2">
              <span class="status-badge status-valid">
                <i class="fas fa-check me-1"></i>
                {{ valid_records_count }} Valid
              </span>
              <span v-if="invalid_records_count > 0" class="status-badge status-invalid">
                <i class="fas fa-times me-1"></i>
                {{ invalid_records_count }} Invalid
              </span>
              <span class="status-badge" style="background: #f3f4f6; color: #374151;">
                {{ extracted_records.length }} Total
              </span>
            </div>
          </div>
          
          <!-- Limit Warning -->
          <div v-if="extracted_records.length > RECORD_LIMIT" class="batch-alert batch-alert-warning mx-4 mt-4">
            <i class="fas fa-exclamation-triangle"></i>
            <div>
              <strong>Record limit exceeded!</strong>
              Your file contains {{ extracted_records.length }} records, but the maximum allowed is {{ RECORD_LIMIT }}.
              Please reduce your file or split into multiple batches.
            </div>
          </div>

          <!-- Duplicate Warning - MessageCard Style -->
          <div v-if="has_duplicates" class="duplicate-warning-card mx-4 mt-4">
            <div class="card border-danger">
              <div class="card-body text-center py-4">
                <i class="fas fa-copy text-danger fa-2x mb-3"></i>
                <h6 class="text-danger fw-bold mb-2">
                  Duplicate {{ batch_type === 'individual' ? 'ID Numbers' : 'Registration Numbers' }} Detected
                </h6>
                <p class="text-xs text-muted mb-3">
                  Found {{ unique_duplicate_identifiers.length }} duplicate {{ batch_type === 'individual' ? 'ID number(s)' : 'registration number(s)' }} 
                  affecting {{ duplicate_records.length }} records.<br>
                  Processing cannot continue until duplicates are removed.
                </p>
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-danger rounded-3 px-4"
                  @click="open_duplicates_modal">
                  <span class="d-flex">
                    View Duplicates
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div class="p-3">
            <CustomTable
              :fetchData="fetch_preview_data"
              :columns="preview_columns"
              :hasActions="false"
              :showSearch="false"
              tableKey="batch-preview-table"
              ref="previewTable"
            >
              <!-- Individual Fields -->
              <template v-if="batch_type === 'individual'" #cell-id_number="{ row }">
                <span :class="{'text-danger': !row?.id_number || (row?._validation && !row?._validation?.id_valid)}">
                  {{ row?.id_number || '—' }}
                </span>
                <div v-if="row?._validation?.id_error" class="text-danger text-xs mt-1">
                  <i class="fas fa-exclamation-circle me-1"></i>
                  {{ row?._validation?.id_error }}
                </div>
              </template>
              
              <template v-if="batch_type === 'individual'" #cell-first_name="{ row }">
                <span :class="{'text-danger': !row?.first_name}">
                  {{ row?.first_name || '—' }}
                </span>
              </template>
              
              <template v-if="batch_type === 'individual'" #cell-last_name="{ row }">
                <span :class="{'text-danger': !row?.last_name}">
                  {{ row?.last_name || '—' }}
                </span>
              </template>

              <!-- Company Fields -->
              <template v-if="batch_type === 'company'" #cell-name="{ row }">
                <span :class="{'text-danger': !row?.name}">
                  {{ row?.name || '—' }}
                </span>
              </template>
              
              <template v-if="batch_type === 'company'" #cell-registration_number="{ row }">
                <span :class="{'text-danger': !row?.registration_number}">
                  {{ row?.registration_number || '—' }}
                </span>
                <div v-if="row?._validation?.validation_error" class="text-danger text-xs mt-1">
                  <i class="fas fa-exclamation-circle me-1"></i>
                  {{ row?._validation?.validation_error }}
                </div>
              </template>

              <template #cell-zoho_record_id="{ row }">
                <span class="text-muted text-sm">
                  {{ row?.zoho_record_id || '—' }}
                </span>
              </template>

              <!-- Status Column (shared) -->
              <template #cell-status="{ row }">
                <template v-if="batch_type === 'individual'">
                  <span v-if="row?._validation?.all_valid" class="status-badge status-valid">
                    <i class="fas fa-check-circle me-1"></i> Valid
                  </span>
                  <span v-else-if="row?._validation && !row?._validation?.all_valid && row?.id_number" class="status-badge status-invalid">
                    <i class="fas fa-times-circle me-1"></i> Invalid
                  </span>
                  <span v-else class="status-badge status-incomplete">
                    <i class="fas fa-exclamation-circle me-1"></i> Incomplete
                  </span>
                </template>
                <template v-else>
                  <span v-if="row?._validation?.all_valid" class="status-badge status-valid">
                    <i class="fas fa-check-circle me-1"></i> Valid
                  </span>
                  <span v-else-if="row?._validation && !row?._validation?.all_valid && row?._has_any_data" class="status-badge status-invalid">
                    <i class="fas fa-times-circle me-1"></i> Invalid
                  </span>
                  <span v-else class="status-badge status-incomplete">
                    <i class="fas fa-exclamation-circle me-1"></i> Incomplete
                  </span>
                </template>
              </template>
            </CustomTable>
          </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="extracted_records.length > 0" class="d-flex justify-content-end gap-3 mt-4">
          <button class="btn btn-sm btn-outline-secondary rounded-3 shadow-sm fw-bold px-4" @click="reset_upload">
            <i class="fas fa-redo me-2"></i>
            Reset
          </button>
          <button 
            class="btn btn-sm bg-green rounded-3 shadow-sm text-white fw-bold px-4"
            :disabled="!can_start_processing || batch_processing"
            @click="start_batch_processing">
            <i :class="batch_processing ? 'fas fa-spinner fa-spin' : 'fas fa-play'" class="me-2"></i>
            <span v-if="batch_processing">Starting...</span>
            <span v-else>Start Processing ({{ valid_records_count }} records)</span>
          </button>
        </div>
      </div>

      <!-- Processing Tab Content -->
      <div v-if="current_tab === 'processing'" class="tab-content mt-4">
        <!-- No Active Job -->
        <div v-if="!active_batch_job && !loading_batch_status" class="batch-card p-5 text-center">
            <div class="card shadow-none border-0 rounded-3">
                <div class="card-body">
                    <div class="empty-state-icon mb-3">
                        <i class="fas fa-cogs fa-2x text-muted"></i>
                    </div>
                    <h6 class="fw-bold mb-2">No Active Batch Job</h6>
                    <p class="text-muted text-sm mb-3">Upload a file in the Upload tab to start batch processing</p>
                    <button class="btn btn-sm bg-green rounded-3 shadow-sm text-white fw-bold px-4" @click="current_tab = 'upload'">
                        <i class="fas fa-upload me-2"></i>
                        Go to Upload
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading_batch_status" class="text-center py-5">
          <div class="spinner-border text-primary mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted text-sm">Loading batch status...</p>
        </div>

        <!-- Active Batch Job -->
        <div v-if="active_batch_job && !loading_batch_status">
          <!-- Progress Header -->
          <div class="progress-header mb-4">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5 class="fw-bold mb-1">
                  <i :class="batch_status_icon" class="me-2"></i>
                  {{ batch_type === 'individual' ? 'Individual Verification' : 'Company Onboarding' }}
                </h5>
                <p class="text-sm opacity-75 mb-0">
                  <span class="me-3">Workflow: <strong>{{ current_workflow_name }}</strong></span>
                  <span v-if="last_updated">Last updated: {{ last_updated }}</span>
                </p>
              </div>
              <div class="d-flex align-items-center gap-2">
                <span :class="['status-badge', 'status-' + batch_status]">
                  {{ batch_status_text }}
                </span>
                <button 
                  class="btn btn-sm btn-light rounded-pill" 
                  @click="fetch_batch_status"
                  :disabled="fetching_status">
                  <i :class="fetching_status ? 'fas fa-spinner animate-spin' : 'fas fa-sync-alt'" class="text-xs"></i>
                </button>
              </div>
            </div>

            <!-- Progress Bar -->
            <div class="batch-progress mb-3">
              <div 
                class="batch-progress-bar"
                :style="{ width: overall_progress + '%' }">
              </div>
            </div>
            <div class="text-end text-sm opacity-75">{{ overall_progress }}% Complete</div>

            <!-- Stats Grid -->
            <div class="progress-stats">
              <div class="stat-item">
                <div class="stat-value">{{ total_records }}</div>
                <div class="stat-label">Total</div>
              </div>
              <div class="stat-item">
                <div class="stat-value text-info">{{ processing_count }}</div>
                <div class="stat-label">Processing</div>
              </div>
              <div class="stat-item">
                <div class="stat-value text-success">{{ successful_count }}</div>
                <div class="stat-label">Completed</div>
              </div>
              <div class="stat-item">
                <div class="stat-value text-danger">{{ failed_count }}</div>
                <div class="stat-label">Failed</div>
              </div>
            </div>
          </div>

          <!-- Records Processing Table -->
          <div class="batch-card">
            <div class="d-flex justify-content-between align-items-center p-4 border-bottom">
              <h6 class="fw-bold text-xs mb-0">
                <i class="fas fa-list me-2 text-primary"></i>
                Processing Details
              </h6>
              <div v-if="retryable_count > 0" class="text-warning text-xs">
                <i class="fas fa-redo me-1"></i>
                {{ retryable_count }} records available for retry
              </div>
            </div>

            <div class="p-3">
              <CustomTable
                :fetchData="fetch_processing_data"
                :columns="processing_columns"
                :hasActions="true"
                :showSearch="false"
                tableKey="batch-processing-table"
                ref="processingTable"
              >
                <!-- Name Column (Individual) -->
                <template v-if="batch_type === 'individual'" #cell-name="{ row }">
                  <div class="fw-medium">{{ row?.first_name || '—' }} {{ row?.last_name || '' }}</div>
                </template>

                <!-- ID Number Column (Individual) -->
                <template v-if="batch_type === 'individual'" #cell-id_number="{ row }">
                  <span class="text-muted text-sm">{{ row?.id_number || '—' }}</span>
                </template>

                <!-- Company Name Column (Company) -->
                <template v-if="batch_type === 'company'" #cell-company_name="{ row }">
                  <div class="fw-medium">{{ row?.company_name || '—' }}</div>
                </template>

                <!-- Registration Number Column (Company) -->
                <template v-if="batch_type === 'company'" #cell-registration_number="{ row }">
                  <span class="text-muted text-sm">{{ row?.registration_number || '—' }}</span>
                </template>

                <!-- Status Column (Shared) -->
                <template #cell-status="{ row }">
                  <span :class="['status-badge', 'status-' + get_status_class(row?.status)]">
                    <i :class="get_status_icon(row?.status)" class="me-1"></i>
                    {{ row?.status || '—' }}
                  </span>
                </template>

                <!-- Current Service Column -->
                <template #cell-current_service="{ row }">
                  <span v-if="row?.current_service" class="text-sm text-primary">
                    {{ row?.current_service }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </template>

                <!-- Progress Column -->
                <template #cell-progress="{ row }">
                  <div v-if="row?.total_services > 0" class="d-flex align-items-center gap-2">
                    <div class="service-progress-mini">
                      <div 
                        class="service-progress-mini-bar" 
                        :style="{ width: (row?.progress_percentage || 0) + '%' }">
                      </div>
                    </div>
                    <span class="text-xs text-muted">{{ row?.completed_services || 0 }}/{{ row?.total_services || 0 }}</span>
                  </div>
                  <div v-else-if="row?.status === 'processing'" class="d-flex align-items-center gap-2">
                    <i class="fas fa-spinner animate-spin text-primary"></i>
                    <span class="text-xs">Starting...</span>
                  </div>
                  <span v-else class="text-muted">—</span>
                </template>

                <!-- Error Column -->
                <template #cell-error="{ row }">
                  <div v-if="row?.error_message" class="text-danger text-xs">
                    <i class="fas fa-exclamation-triangle me-1"></i>
                    {{ row?.error_message }}
                  </div>
                  <span v-else class="text-muted">—</span>
                </template>

                <!-- Actions Column -->
                <template #actions="{ row }">
                  <button 
                    v-if="row?.status === 'failed'"
                    class="btn-retry"
                    @click="retry_record(row, batch_records.indexOf(row))"
                    :disabled="retrying_index === batch_records.indexOf(row)">
                    <i :class="retrying_index === batch_records.indexOf(row) ? 'fas fa-spinner animate-spin' : 'fas fa-redo'"></i>
                    Retry
                  </button>
                  <span v-else-if="row?.status === 'completed'" class="text-success text-xs">
                    <i class="fas fa-check-circle"></i>
                  </span>
                  <span v-else-if="row?.status === 'processing'" class="text-primary text-xs">
                    <i class="fas fa-spinner animate-spin"></i>
                  </span>
                  <span v-else class="text-muted">—</span>
                </template>
              </CustomTable>
            </div>
          </div>

          <!-- Completion Actions -->
          <div v-if="batch_status === 'completed' || batch_status === 'failed'" class="d-flex justify-content-end gap-3 mt-4">
            <button class="btn btn-sm bg-green rounded-3 shadow-sm text-white fw-bold px-4" @click="start_new_batch">
              <i class="fas fa-plus me-2"></i>
              New Batch
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Duplicates Modal -->
  <div 
    class="modal fade" 
    :class="{ 'show d-block': show_duplicates_modal }"
    tabindex="-1"
    @click.self="close_duplicates_modal">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content border-0 shadow-lg rounded-4">
        <!-- Clean Modal Header - MessageCard Style -->
        <div class="modal-header border-0 pb-0 pt-4 px-4">
          <button type="button" class="btn-close" @click="close_duplicates_modal"></button>
        </div>

        <!-- Modal Body - MessageCard Style -->
        <div class="modal-body text-center pt-0 px-4">
          <!-- Icon -->
          <div class="duplicate-modal-icon mb-3">
            <i class="fas fa-copy text-danger fa-3x"></i>
          </div>
          
          <!-- Title -->
          <h5 class="fw-bold text-dark mb-4">Duplicate Records Detected</h5>

          <!-- CustomTable for Duplicates -->
          <div class="duplicates-table-container text-start">
            <CustomTable
              :fetchData="fetch_duplicates_data"
              :columns="duplicate_columns"
              :hasActions="false"
              :showSearch="false"
              tableKey="batch-duplicates-table"
              ref="duplicatesTable"
            >
              <!-- Row Number Column -->
              <template #cell-row_number="{ row }">
                <span class="text-muted text-sm">{{ row?.row_number }}</span>
              </template>

              <!-- Identifier Column (ID/Registration) -->
              <template #cell-identifier="{ row }">
                <span class="fw-medium text-danger">
                  <i class="fas fa-exclamation-triangle me-1 text-warning small"></i>
                  {{ row?.identifier || '—' }}
                </span>
              </template>

              <!-- Name Column -->
              <template #cell-name="{ row }">
                <span class="text-muted">{{ row?.name || '—' }}</span>
              </template>

              <!-- Occurrence Count Column -->
              <template #cell-occurrence_count="{ row }">
                <span class="badge bg-danger rounded-pill">{{ row?.occurrence_count }}x</span>
              </template>
            </CustomTable>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer border-0 pt-0 pb-4 px-4 justify-content-center">
          <div class="text-center">
            <p class="text-muted text-xs mb-3">
              <i class="fas fa-lightbulb text-warning me-1"></i>
              Tip: Remove duplicates in Excel/CSV before re-uploading
            </p>
            <button type="button" class="btn btn-danger rounded-3 px-5" @click="close_duplicates_modal">
              <i class="fas fa-check me-2"></i>
              Understood
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Modal Backdrop -->
  <div v-if="show_duplicates_modal" class="modal-backdrop fade show" @click="close_duplicates_modal"></div>
</div>
</template>
<script src="./BatchProcessing.js"></script>

<style scoped>
/* CSS Variables for theming */
:root {
  --batch-primary: #3b82f6;
  --batch-success: #10b981;
  --batch-warning: #f59e0b;
  --batch-danger: #ef4444;
  --batch-info: #06b6d4;
  --batch-muted: #6b7280;
}

.batch-processing-container{
    height: 85vh;
}

/* Card Styling */
.batch-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background: white;
  transition: box-shadow 0.2s ease;
}

/* Upload Zone */
.upload-zone {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  transition: all 0.3s ease;
  min-height: 200px;
}

.upload-zone:hover {
  border-color: var(--batch-primary);
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.upload-zone.drag-over {
  border-color: var(--batch-primary);
  background: #dbeafe;
  transform: scale(1.01);
}

.upload-zone.has-file {
  border-style: solid;
  border-color: var(--batch-success);
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
}

/* File Info Card */
.file-info-card {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Column Mapping Section */
.mapping-section {
  background: #f8fafc;
  border-radius: 8px;
  padding: 1.25rem;
  border: 1px solid #e2e8f0;
}

/* Preview Table */
.preview-container {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.preview-table {
  margin-bottom: 0;
}

.preview-table thead {
  background: #1f2937;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.preview-table th {
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: none;
}

.preview-table tbody tr {
  transition: background-color 0.15s ease;
}

.preview-table tbody tr:hover {
  background-color: #f3f4f6;
}

.preview-table td {
  padding: 0.75rem 1rem;
  vertical-align: middle;
  font-size: 0.875rem;
  border-bottom: 1px solid #f3f4f6;
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-valid {
  background: #dcfce7;
  color: #166534;
}

.status-invalid {
  background: #fee2e2;
  color: #991b1b;
}

.status-incomplete {
  background: #fef3c7;
  color: #92400e;
}

.status-processing {
  background: #dbeafe;
  color: #1e40af;
}

.status-completed {
  background: #dcfce7;
  color: #166534;
}

.status-failed {
  background: #fee2e2;
  color: #991b1b;
}

.status-pending {
  background: #f3f4f6;
  color: #374151;
}

/* Progress Section */
.progress-header {
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 12px;
  padding: 1.5rem;
  color: white;
}

.progress-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-top: 1rem;
}

.stat-item {
  text-align: center;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.stat-label {
  font-size: 0.75rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Progress Bar */
.batch-progress {
  height: 12px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.batch-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
  border-radius: 6px;
  transition: width 0.5s ease;
}

/* Action Buttons */
.btn-batch-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: none;
  color: white;
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.btn-batch-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.btn-batch-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-batch-secondary {
  background: white;
  border: 1px solid #e5e7eb;
  color: #374151;
  padding: 0.625rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.btn-batch-secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-batch-danger {
  background: white;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.75rem;
  transition: all 0.2s ease;
}

.btn-batch-danger:hover {
  background: #fef2f2;
  border-color: #f87171;
}

/* Animations */
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Scrollbar Styling */
.preview-scroll {
  max-height: 400px;
  overflow-y: auto;
}

.preview-scroll::-webkit-scrollbar {
  width: 6px;
}

.preview-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.preview-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.preview-scroll::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem 1.5rem;
  color: #6b7280;
}

.empty-state-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Alert Styling */
.batch-alert {
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.batch-alert-info {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
}

.batch-alert-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.batch-alert-danger {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.batch-alert-success {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
}

/* Responsive */
@media (max-width: 768px) {
  .progress-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .stat-value {
    font-size: 1.25rem;
  }
}

/* Tab Navigation Override */
.batch-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.batch-tab {
  padding: 0.75rem 1.25rem;
  border-radius: 8px 8px 0 0;
  font-weight: 500;
  font-size: 0.875rem;
  color: #6b7280;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.batch-tab:hover {
  color: #374151;
  background: #f9fafb;
}

.batch-tab.active {
  color: #3b82f6;
  background: #eff6ff;
}

.batch-tab.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: #3b82f6;
  border-radius: 2px 2px 0 0;
}

/* Processing Table */
.processing-table {
  width: 100%;
}

.processing-table thead {
  background: #f8fafc;
}

.processing-table th {
  padding: 0.875rem 1rem;
  font-weight: 600;
  font-size: 0.75rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid #e2e8f0;
}

.processing-table td {
  padding: 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;
}

.processing-table tbody tr:last-child td {
  border-bottom: none;
}

/* Service Progress Mini */
.service-progress-mini {
  width: 100px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.service-progress-mini-bar {
  height: 100%;
  background: #3b82f6;
  border-radius: 3px;
  transition: width 0.3s ease;
}

/* Retry Button */
.btn-retry {
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #92400e;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  transition: all 0.2s ease;
}

.btn-retry:hover {
  background: #fde68a;
  border-color: #f59e0b;
}

/* Live indicator */
.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.5rem;
  background: #dcfce7;
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
  color: #166534;
  text-transform: uppercase;
}

.live-dot {
  width: 6px;
  height: 6px;
  background: #16a34a;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

/* Duplicate Warning Card - MessageCard Style */
.duplicate-warning-card .card {
  border-radius: 12px;
  border-width: 1px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background: #fff;
}

.duplicate-warning-card .card-body {
  padding: 2rem 1.5rem;
}

.duplicate-warning-card .fa-2x {
  opacity: 0.9;
}

.duplicate-warning-card h6 {
  font-size: 0.95rem;
}

.duplicate-warning-card p {
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

/* Duplicates Modal Styling - Clean MessageCard Style */
.duplicate-modal-icon {
  opacity: 0.9;
}

.duplicates-table-container {
  overflow: hidden;
  margin-top: 1rem;
}

/* Modal Animation */
.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Fix z-index for modal */
.modal {
  z-index: 1050;
}

.modal-backdrop {
  z-index: 1040;
}
</style>